import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import ConfirmationModalContainer from '../../../../ConfirmationModal/ConfirmationModalContainer';
import { setModalContent } from '../../../../../redux/modules/modalEvent';
import MiniCheckBox from 'src/components/modules/MiniCheckBox/MiniCheckBox';
import {
  OnboardingActionStatus,
  PauseOnboardingModalButtonLabels,
  RadioButtonOptions,
} from './PausedOnboardingModalConstant';
import { UserTypes } from 'src/latest/src';
import { useTranslation } from 'react-i18next';

type Props = {
  handleOnboardingStatus: (note: string, pausedReason: string | null) => void;
  onboardingActionStatus:
    | OnboardingActionStatus.PAUSE_ONBOARDING
    | OnboardingActionStatus.RESUME_ONBOARDING
    | OnboardingActionStatus.CANCEL_ONBOARDING
    | OnboardingActionStatus.ONBOARDING_COMPLETE;
  userType?: string;
};

export const PauseOnboardingModal: React.FC<Props> = ({
  handleOnboardingStatus,
  onboardingActionStatus,
  userType = '',
}) => {
  const { t } = useTranslation('therapistClientProfileModals');
  const dispatch = useDispatch();
  const [note, setNote] = useState('');
  const [updateStatus, setUpdateStatus] = useState(false);
  const [radioButtonValue, setRadioButtonValue] = useState<string | null>(null);
  const [radioButtonSelected, setRadioButtonSelected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setUpdateStatus(false);
  }, []);

  const editNoteContent = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  };

  function getDynamicText(): string {
    switch (onboardingActionStatus) {
      case OnboardingActionStatus?.RESUME_ONBOARDING:
        return t('pauseOnboardingModal.descriptionContent.resumeOnboarding');
      case OnboardingActionStatus?.PAUSE_ONBOARDING:
        return t('pauseOnboardingModal.descriptionContent.pauseOnboarding');
      case OnboardingActionStatus?.CANCEL_ONBOARDING:
        if (userType === UserTypes?.CLIENT) {
          return '';
        }
        
        return t('pauseOnboardingModal.descriptionContent.cancelOnboarding');
      default:
        return '';
    }
  }

  const handleSubmitUpdateOnboardingStatus = async () => {
    setIsProcessing(true);

    if (!updateStatus) {
      await handleOnboardingStatus(note, radioButtonValue);
    }

    setUpdateStatus(true);
    setIsProcessing(false);
  };

  const handleRadioButtonChange = (value: string | null) => {
    setRadioButtonValue(value);
    setRadioButtonSelected(true);
  };

  const renderRadioButtonOptions = () => (
    <div className="onboarding-radio-btn">
      <div className="onboarding-radio-btn-txt">
        <p>{t('pauseOnboardingModal.radioButtonPrompt')}</p>
      </div>
      <div className="onboarding-radio-btn-options">
        {RadioButtonOptions.map((option, index) => (
          <MiniCheckBox
            key={index}
            className="onboarding-radio-btn-checkbox"
            text={t(option.label)}
            isCheckFirst={true}
            isActive={radioButtonValue === option.value}
            onClick={() => handleRadioButtonChange(option.value)}
            value={option.value}
            isRadio={true}
          />
        ))}
      </div>
    </div>
  );

  const renderModalContent = () => (
    <div className="display-grid justify-content-center align-items-center">
      {userType === UserTypes.CLIENT && renderRadioButtonOptions()}
      <div>
        <textarea
          onChange={editNoteContent}
          value={note}
          style={{ height: '150px' }}
          placeholder={t('pauseOnboardingModal.addNotePlaceholder')}
          maxLength={500}
        />
      </div>
    </div>
  );

  return (
    <ConfirmationModalContainer
      hasNoButton={
        onboardingActionStatus === OnboardingActionStatus.RESUME_ONBOARDING
      }
      clickedYes={handleSubmitUpdateOnboardingStatus}
      clickedOnNo={() => dispatch(setModalContent({ modalContent: null }))}
      dynamicText={getDynamicText()}
      children={
        onboardingActionStatus !== OnboardingActionStatus.RESUME_ONBOARDING &&
        renderModalContent()
      }
      className="pb-0"
      primaryButtonVariant={
        onboardingActionStatus === OnboardingActionStatus.RESUME_ONBOARDING
          ? 'success'
          : 'danger'
      }
      yesButtonText={
        onboardingActionStatus === OnboardingActionStatus.RESUME_ONBOARDING ||
        onboardingActionStatus === OnboardingActionStatus.CANCEL_ONBOARDING
          ? 'Yes'
          : t(PauseOnboardingModalButtonLabels[onboardingActionStatus])
      }
      hasRadioButtonSelected={radioButtonSelected}
      userType={userType}
      isProcessing={isProcessing}
    />
  );
};
